import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription, map } from 'rxjs';

export type Icon =
  | 'camera'
  | 'dock'
  | 'draw'
  | 'filter'
  | 'flip-horizontal'
  | 'flip-vertical'
  | 'minimize'
  | 'more'
  | 'resize'
  | 'settings'
  | 'sticker';

function toPixelString(numStr: number | string): string {
  if (typeof numStr === 'number') {
    return `${numStr}px`;
  }

  return numStr;
}

/**
 * Loads icons from the assets folder assets/img/icons/ with prefix "icon_" and suffix ".svg" and displays those as svg.
 *
 * Example:
 * icon = 'dock'
 * path to file: assets/img/icons/icon_dock.svg
 */

@Component({
  selector: 'app-asset-icon',
  template:
  '<span #container class="asset-icon" [style.width]="width" [style.height]="height" [innerHTML]="svgIcon"></span>',
  styleUrls: ['./asset-icon.component.scss'],
  })
export class AssetIconComponent implements OnDestroy {
  @Input() icon: Icon;
  @Input() width: string | number = 24;
  @Input() height: string | number = 24;
  @ViewChild('container', { static: true })
  container: ElementRef<HTMLSpanElement>;

  public svgIcon: any;
  private subscription?: Subscription;
  private readonly URL_TEMPLATE_STRING = 'assets/img/icons/icon_{icon_name}.svg';

  constructor(
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public ngOnChanges(): void {
    this.svgIcon = '';

    if (!this.icon) {
      return;
    }

    const url = this.buildURL(this.icon);

    this.subscription?.unsubscribe();
    this.element.innerHTML = '';

    this.subscription = this.httpClient
      .get(url, { responseType: 'text' })
      .pipe(map((value) => this.sanitizer.bypassSecurityTrustHtml(value)))
      .subscribe(async (html) => {
        this.svgIcon = html;

        // Wait for browser to render the element
        while (!this.element.hasChildNodes()) {
          await new Promise((res) => setTimeout(res, 100));
        }

        // Does nothing when subscription has closed (icon has changed or component is destroyed)
        this.svg.setAttribute('width', toPixelString(this.width));
        this.svg.setAttribute('height', toPixelString(this.height));
      });
  }

  private get element(): HTMLSpanElement {
    return this.container.nativeElement;
  }

  private get svg(): SVGSVGElement {
    return this.element.children.item(0) as SVGSVGElement;
  }

  private buildURL(icon: Icon): string {
    return this.URL_TEMPLATE_STRING.replace('{icon_name}', icon);
  }
}
